/**
 * The answering machine's moving parts. The page is readable without this file (the tape
 * renders server-side); what loads here is everything that makes noise -- the handset
 * flow (pick up -> record -> review -> send), playback of the tape, the sound effects,
 * and the phosphor crawl in the unit's display window.
 *
 * The admin buttons are wired here too, but /api/admin/* re-checks the session on every
 * request -- this file is convenience, not the security boundary.
 */

const $ = (id) => document.getElementById(id);

const crawlEl = $('crawl');
const machine = $('machine');
const meter = $('meter');
const rows = [...document.querySelectorAll('.handset__row')];

const MAX_SECONDS = 60;

/* --------------------------------------------------------------- Sounds */

const sounds = {
  pickup: new Audio('/media/voicemail/pick-up.mp3'),
  hangup: new Audio('/media/voicemail/hang-up.mp3'),
  beep: new Audio('/media/voicemail/beep.mp3'),
};

/** Daniel's outgoing message -- what you get instead of Daniel. */
const greeting = new Audio('/media/voicemail/greeting.mp3');

function stopGreeting() {
  greeting.pause();
  greeting.currentTime = 0;
}

/** Play an effect and resolve when it ends -- the beep must FINISH before tape rolls. */
const playEffect = (audio) =>
  new Promise((resolve) => {
    audio.currentTime = 0;
    audio.onended = resolve;
    audio.play().catch(resolve); // if the effect can't play, the show goes on
  });

/* ---------------------------------------------------------------- Crawl */

/**
 * The display window. Scrolling text is the content twice over with a keyframe sliding
 * -50%: one copy's width, so the loop is seamless. Static text (countdowns, verdicts)
 * just sits there like a real VFD would hold a reading.
 */
function crawl(text) {
  const copy = `${text}  ···  `;
  crawlEl.textContent = copy + copy;
  crawlEl.classList.remove('machine__crawl--static');
  crawlEl.style.setProperty('--crawl-ms', `${Math.max(8, copy.length * 0.42) * 1000}ms`);
}

function hold(text) {
  crawlEl.textContent = text;
  crawlEl.classList.add('machine__crawl--static');
}

function idleCrawl() {
  const n = Number(machine.dataset.count) || 0;
  const count = n === 1 ? 'YOU HAVE 1 MESSAGE' : `YOU HAVE ${n} MESSAGES`;
  crawl(`AUTOMATIC TELEPHONE ANSWERING SYSTEM ··· ${count} ··· PICK UP TO LEAVE ONE`);
}

/* -------------------------------------------------------------- Handset */

const setState = (name) => {
  rows.forEach((row) => {
    row.hidden = row.dataset.state !== name;
  });
};

let recorder = null;
let micStream = null;
let chunks = [];
let recording = null; // { blob, seconds } once something's on tape
let recordedAt = 0;
let ticker = null;

function releaseMic() {
  micStream?.getTracks().forEach((t) => t.stop());
  micStream = null;
}

function pickMime() {
  if (!window.MediaRecorder) return null;
  return (
    ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find((t) =>
      MediaRecorder.isTypeSupported(t),
    ) ?? ''
  );
}

$('pickup').addEventListener('click', async () => {
  await playEffect(sounds.pickup);
  setState('open');
  crawl('RINGING DANIEL ··· NO ANSWER ··· OUTGOING MESSAGE');
  greeting.onended = () => crawl('LEAVE A MESSAGE AFTER THE BEEP');
  greeting.currentTime = 0;
  greeting.play().catch(() => crawl('LEAVE A MESSAGE AFTER THE BEEP'));
});

$('record').addEventListener('click', async () => {
  stopGreeting(); // pressing the button skips the rest of the spiel
  const mime = pickMime();
  if (mime === null) {
    hold('THIS BROWSER CANNOT RECORD');
    return;
  }
  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    hold('MICROPHONE BLOCKED · CHECK PERMISSIONS');
    return;
  }

  chunks = [];
  recorder = new MediaRecorder(micStream, mime ? { mimeType: mime } : undefined);
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  recorder.onstop = () => {
    clearInterval(ticker);
    releaseMic();
    const seconds = Math.min(MAX_SECONDS, Math.max(1, Math.round((Date.now() - recordedAt) / 1000)));
    recording = { blob: new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }), seconds };
    setState('review');
    crawl('END OF RECORDING ··· LISTEN BACK, SEND IT, OR SCRAP IT');
  };

  await playEffect(sounds.beep);
  recorder.start();
  recordedAt = Date.now();
  setState('recording');

  const tick = () => {
    const left = MAX_SECONDS - Math.floor((Date.now() - recordedAt) / 1000);
    if (left <= 0) {
      stopRecording();
      return;
    }
    meter.textContent = `0:${String(left).padStart(2, '0')}`;
    hold(`● REC · 0:${String(left).padStart(2, '0')} REMAINING`);
  };
  tick();
  ticker = setInterval(tick, 250);
});

function stopRecording() {
  if (recorder && recorder.state !== 'inactive') recorder.stop();
}

$('stop').addEventListener('click', stopRecording);

$('preview').addEventListener('click', () => {
  if (!recording) return;
  playMessage(URL.createObjectURL(recording.blob), 'YOUR MESSAGE · UNSENT');
});

$('scrap').addEventListener('click', () => {
  recording = null;
  stopPlayback();
  setState('open');
  crawl('MESSAGE SCRAPPED ··· THE TAPE FORGETS ··· LEAVE ANOTHER WHEN READY');
});

$('send').addEventListener('click', async () => {
  if (!recording) return;
  stopPlayback();
  hold('TRANSMITTING…');
  $('send').disabled = true;

  const form = new FormData();
  const ext = recording.blob.type.includes('mp4') ? 'm4a' : 'webm';
  form.append('audio', recording.blob, `voicemail.${ext}`);
  form.append('duration', String(recording.seconds));

  try {
    const res = await fetch('/api/voicemail', { method: 'POST', body: form });
    if (!res.ok) throw new Error(await res.text().catch(() => '') || `HTTP ${res.status}`);
    recording = null;
    await playEffect(sounds.hangup);
    setState('idle');
    crawl('MESSAGE SAVED ··· IT JOINS THE TAPE ONCE SCREENED ··· THANK YOU FOR CALLING');
  } catch {
    hold('TRANSMISSION FAILED · TRY AGAIN');
  } finally {
    $('send').disabled = false;
  }
});

async function hangUp() {
  stopRecording();
  stopPlayback();
  stopGreeting();
  recording = null;
  releaseMic();
  await playEffect(sounds.hangup);
  setState('idle');
  idleCrawl();
}

$('hangup').addEventListener('click', hangUp);
$('hangup2').addEventListener('click', hangUp);

/* ------------------------------------------------------------- Playback */

const player = new Audio();
let playingRow = null; // the .msg row lit up right now, if any

function rowOf(el) {
  return el.closest('.msg');
}

function resetRow(row) {
  if (!row) return;
  row.classList.remove('msg--playing');
  row.querySelector('.msg__play').textContent = '▶';
  row.querySelector('.msg__fill').style.width = '0%';
}

function stopPlayback() {
  player.pause();
  player.removeAttribute('src');
  resetRow(playingRow);
  playingRow = null;
}

/** One shared element for the whole tape -- a real machine has one speaker. */
async function playMessage(src, label) {
  stopPlayback();
  stopGreeting();
  await playEffect(sounds.beep);
  player.src = src;
  await player.play().catch(() => hold('PLAYBACK FAILED'));
  crawl(`NOW PLAYING ··· ${label}`);
}

player.addEventListener('timeupdate', () => {
  if (!playingRow || !isFinite(player.duration) || !player.duration) return;
  playingRow.querySelector('.msg__fill').style.width = `${(player.currentTime / player.duration) * 100}%`;
});

player.addEventListener('ended', () => {
  resetRow(playingRow);
  playingRow = null;
  idleCrawl();
});

document.querySelectorAll('.msg__play').forEach((btn) => {
  btn.addEventListener('click', async () => {
    const row = rowOf(btn);

    if (playingRow === row) {
      // Same message: toggle. No beep on resume -- the machine already beeped at you.
      if (player.paused) {
        await player.play().catch(() => {});
        btn.textContent = '❚❚';
      } else {
        player.pause();
        btn.textContent = '▶';
        hold('PAUSED');
      }
      return;
    }

    const date = row.querySelector('.msg__date').textContent.trim().toUpperCase();
    await playMessage(`/api/voicemail/${row.dataset.id}/audio`, `MESSAGE OF ${date}`);
    playingRow = row;
    row.classList.add('msg--playing');
    btn.textContent = '❚❚';
  });
});

document.querySelectorAll('.msg__bar').forEach((bar) => {
  bar.addEventListener('click', (e) => {
    const row = rowOf(bar);
    if (row !== playingRow || !isFinite(player.duration) || !player.duration) return;
    const rect = bar.getBoundingClientRect();
    player.currentTime = ((e.clientX - rect.left) / rect.width) * player.duration;
  });
});

/* ---------------------------------------------------------------- Admin */

if (document.body.hasAttribute('data-admin')) {
  const call = async (path, method) => {
    const res = await fetch(path, { method });
    if (!res.ok) {
      hold('THE MACHINE REFUSES');
      throw new Error(`HTTP ${res.status}`);
    }
    location.reload();
  };
  document.querySelectorAll('.msg__approve').forEach((btn) =>
    btn.addEventListener('click', () =>
      call(`/api/admin/voicemail/${rowOf(btn).dataset.id}/approve`, 'POST'),
    ),
  );
  document.querySelectorAll('.msg__delete').forEach((btn) =>
    btn.addEventListener('click', () =>
      call(`/api/admin/voicemail/${rowOf(btn).dataset.id}`, 'DELETE'),
    ),
  );
}

/* ----------------------------------------------------------------- Boot */

$('nojs').hidden = true;
$('panel').hidden = false;
setState('idle');
idleCrawl();
